import { create } from 'zustand';
import { api } from '../services/api';

interface User {
  id: number;
  telegramId: number;
  username?: string;
  firstName: string;
  lastName?: string;
  countryCode: string;
  regionId?: number;
  cityId?: number;
  rating: number;
  reviewsCount: number;
  dealsCount: number;
  isPremium: boolean;
  premiumExpiresAt?: string;
  createdAt?: string;
  subscription?: {
    type: string;
    expiresAt: string;
    status: string;
  } | null;
}

interface AuthState {
  user: User | null;
  isInitialized: boolean;
  isLoading: boolean;
  error: string | null;
  telegramInitData: string | null;
  initAuth: () => Promise<void>;
  setUser: (user: User | null) => void;
  updateUser: (data: Partial<User>) => Promise<void>;
  logout: () => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  isInitialized: false,
  isLoading: false,
  error: null,
  telegramInitData: null,

  initAuth: async () => {
    set({ isLoading: true, error: null });

    try {
      const tg = window.Telegram?.WebApp;
      if (!tg?.initDataUnsafe?.user) {
        // Development mode - use mock user
        const mockUser: User = {
          id: 1,
          telegramId: 123456789,
          username: 'testuser',
          firstName: 'Тест',
          lastName: 'Пользователь',
          countryCode: 'ru',
          rating: 4.5,
          reviewsCount: 10,
          dealsCount: 15,
          isPremium: false,
          createdAt: new Date().toISOString()
        };
        set({ user: mockUser, isInitialized: true, isLoading: false });
        return;
      }

      const telegramUser = tg.initDataUnsafe.user;
      const initData = tg.initData;

      set({ telegramInitData: initData });

      // Try to get existing user
      try {
        const response = await api.get(`/users/${telegramUser.id}`);
        set({ user: response.data.data, isInitialized: true, isLoading: false });
      } catch {
        // Create new user
        const response = await api.post('/users', {
          telegramId: telegramUser.id,
          username: telegramUser.username,
          firstName: telegramUser.first_name,
          lastName: telegramUser.last_name
        });
        set({ user: response.data.data, isInitialized: true, isLoading: false });
      }
    } catch (error) {
      console.error('Auth error:', error);
      set({ error: 'Ошибка авторизации', isInitialized: true, isLoading: false });
    }
  },

  setUser: (user) => {
    set({ user });
  },

  updateUser: async (data) => {
    const { user } = get();
    if (!user) return;

    try {
      const response = await api.put(`/users/${user.telegramId}`, data);
      set({ user: response.data.data });
    } catch (error) {
      console.error('Update user error:', error);
    }
  },

  logout: () => {
    set({ user: null, telegramInitData: null });
  }
}));
